import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { GRID_SNAP_OPTIONS_IN, useDesign } from "../../lib/store";
import { RoomCanvas } from "../canvas/RoomCanvas";
import { Button } from "../ui/Button";
import { ScrollRow } from "../ui/ScrollRow";
import { Sheet } from "../ui/Sheet";
import {
  FURNITURE_CATEGORIES,
  FURNITURE_PRESETS,
  customItemToFurniture,
  presetPreviewFurniture,
  presetToFurniture,
  type FurniturePreset,
} from "../../lib/catalog";
import {
  ArrowDownToLine,
  ArrowUpToLine,
  ChevronDown,
  ChevronUp,
  Grid2x2Check,
  Magnet,
  Ruler,
  History,
  LinkIcon,
  Pencil,
  Plus,
  RotateCw,
  Scaling,
  Sparkles,
  Trash2,
  PanelRight,
  SlidersHorizontal,
  Trash,
  X,
  Compass,
} from "lucide-react";
import { Segmented } from "../ui/Segmented";
import { cn } from "../../lib/cn";
import {
  ContextMenu,
  type ContextMenuItem,
  type ContextMenuPosition,
} from "../ui/ContextMenu";
import { usePremium, type PremiumFeatureKey } from "../../lib/premium";
import { hasAccount, useAuth } from "../../lib/auth";
import type { SignInSurface, UpsellTrigger } from "../../lib/events";
import { track } from "../../lib/telemetry";
import { useAiUsage, shouldShowAiUpsell } from "../../lib/usage";
import { subscribeAiLayoutRequests } from "../../lib/aiLayoutRequests";
import { subscribeFurniturePickerRequests } from "../../lib/furniturePickerRequests";
import {
  isFengShuiExampleConsultation,
  isFengShuiLandingPending,
  useFengShuiOnboarding,
} from "../../lib/fengShuiOnboarding";
import { isRender3dLandingPending, useRender3dOnboarding } from "../../lib/render3dOnboarding";
import { PremiumUpsellOverlay } from "../PremiumUpsellOverlay";
import { CreateAccountSheet } from "../CreateAccountSheet";
import { Render3DButton } from "../render3d/Render3DButton";
import { FengShuiSheet } from "../FengShuiSheet";
import { FengShuiHistorySheet } from "../FengShuiHistorySheet";
import { ImportProductSheet } from "../ImportProductSheet";
import { templateFromProductName } from "../../lib/productImport";
import {
  normalizeSourceDomain,
  productUrlForAnalytics,
} from "../../lib/productUrlAnalytics";
import { NumberStepper } from "../ui/NumberStepper";
import {
  formatArea,
  formatLength,
  inwardWallNormal,
  nearestWallSegment,
  polygonArea,
  rotationToFaceDirection,
} from "../../lib/geom";
import { wallLength } from "../../lib/wallGeom";
import { resolveHostSegment } from "../../lib/interiorWalls";
import { formatRelative } from "../../lib/formatTime";
import { Stat } from "../ui/Stat";
import { useCoarsePointer, useIsMobile, useLayoutMode } from "../../lib/useViewportSize";
import { TOUCH_ADJUST_MODE_FLAG, useFlag } from "../../lib/flags";
import { AdjustModeBar } from "../inspector/AdjustModeBar";
import type { KnobField } from "../canvas/FurnitureHandles";
import { useTopbarSlot } from "../../lib/topbarSlot";
import { FurniturePreview } from "../FurniturePreview";
import { FurnitureShapeSection } from "../inspector/FurnitureShapeSection";
import { FurnitureMaterialSection } from "../inspector/FurnitureMaterialSection";
import { InspectorSection } from "../inspector/InspectorSection";
import { ListingProvenance } from "../inspector/ListingProvenance";
import { PieceModelStatus } from "../inspector/PieceModelStatus";
import { resolveFrontKind } from "../../lib/furniture/resolve";
import { TEMPLATES } from "../../lib/furniture/templates";
import { MinimizableInspector } from "../ui/MinimizableInspector";
import {
  DesktopSidePanel,
  SelectionPanelHeader,
} from "../ui/ContextSidePanel";
import type { Furniture } from "../../lib/types";
import { useProducts } from "../../lib/products";
import { captureSource } from "../../lib/productCapture";
import { useCustomFurniture } from "../../lib/customFurniture";
import { FurnitureCreatorModal } from "../FurnitureCreatorModal";
import type {
  CustomFurnitureItem,
  FurnitureCategory,
  ImportedProduct,
  Unit,
  WallMountCategory,
} from "../../lib/types";

type PickerCategory = FurnitureCategory | "imports";

/** The tool row and floating buttons that sit over the canvas edges. */
const CANVAS_CHROME = {
  top: "var(--canvas-inset-top)",
  bottom: "var(--canvas-inset-bottom)",
} as const;
/** While a landing's hero card (feng shui, 3D) replaces the phone tool row. */
const LANDING_CHROME = {
  top: "calc(var(--topbar-h) + var(--top-stack-h) + 1rem)",
  bottom: "var(--canvas-inset-bottom)",
} as const;
/** Desktop: the landing's 360px hero card sits over the canvas's left edge. */
const LANDING_CHROME_DESKTOP = { ...CANVAS_CHROME, left: "calc(360px + 2rem)" } as const;

function formatGridSnap(inches: number): string {
  if (Math.abs(inches - 1) < 1e-6) return "1″";
  if (Math.abs(inches - 1 / 2) < 1e-6) return "½″";
  if (Math.abs(inches - 1 / 3) < 1e-6) return "⅓″";
  if (Math.abs(inches - 1 / 4) < 1e-6) return "¼″";
  return `${inches.toFixed(2)}″`;
}

function snapStepOptionId(inches: number): string {
  if (Math.abs(inches - 1) < 1e-6) return "1";
  if (Math.abs(inches - 1 / 2) < 1e-6) return "1-2";
  if (Math.abs(inches - 1 / 3) < 1e-6) return "1-3";
  if (Math.abs(inches - 1 / 4) < 1e-6) return "1-4";
  return inches.toFixed(2).replace(".", "-");
}

function snapStepHint(inches: number): string {
  const cellsPerInch = 1 / inches;
  if (Math.abs(cellsPerInch - 1) < 1e-6) return "1×/in";
  if (Math.abs(cellsPerInch - 2) < 1e-6) return "2×/in";
  if (Math.abs(cellsPerInch - 3) < 1e-6) return "3×/in";
  if (Math.abs(cellsPerInch - 4) < 1e-6) return "4×/in";
  return `${cellsPerInch.toFixed(1)}×/in`;
}

function isSameSnap(a: number, b: number): boolean {
  return Math.abs(a - b) < 1e-6;
}

/** A labelled on/off row for the mobile Tools sheet. */
function ToolToggle({
  label,
  description,
  checked,
  onChange,
  children,
}: {
  label: string;
  description: string;
  checked: boolean;
  onChange: () => void;
  children?: React.ReactNode;
}) {
  return (
    <div className="rounded-2xl bg-ink-50 ring-1 ring-ink-100 p-3">
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={onChange}
        className="flex w-full min-h-11 items-center gap-3 text-left"
      >
        <div className="min-w-0 flex-1">
          <div className="text-body font-semibold text-ink-900">{label}</div>
          <div className="text-body-sm text-ink-500">{description}</div>
        </div>
        <span
          aria-hidden
          className={cn(
            "relative h-7 w-12 shrink-0 rounded-full transition-colors",
            checked ? "bg-ink-900" : "bg-ink-300"
          )}
        >
          <span
            className={cn(
              "absolute top-0.5 size-6 rounded-full bg-white shadow transition-all",
              checked ? "left-[22px]" : "left-0.5"
            )}
          />
        </span>
      </button>
      {children}
    </div>
  );
}

/** Which AI feature a visitor tried to open. Sign-in resumes into it. */
type AiIntent =
  | { kind: "layout"; surface?: SignInSurface }
  | { kind: "import" };

function signInSurfaceFor(intent: AiIntent): SignInSurface {
  return intent.kind === "import" ? "import_sheet" : (intent.surface ?? "ai_sheet");
}

export function DesignView() {
  const room = useDesign((s) => s.design.room);
  const furniture = useDesign((s) => s.design.furniture);
  const consultations = useDesign((s) => s.design.consultations);
  const unit = useDesign((s) => s.design.unit);
  const addFurniture = useDesign((s) => s.addFurniture);
  const addMount = useDesign((s) => s.addMount);
  const setActiveWallIndex = useDesign((s) => s.setActiveWallIndex);
  const setView = useDesign((s) => s.setView);
  const updateFurniture = useDesign((s) => s.updateFurniture);
  const removeFurniture = useDesign((s) => s.removeFurniture);
  const reorderFurniture = useDesign((s) => s.reorderFurniture);
  const selectedId = useDesign((s) => s.selectedFurnitureId);
  const selectFurniture = useDesign((s) => s.selectFurniture);
  const snapEnabled = useDesign((s) => s.snapEnabled);
  const toggleSnap = useDesign((s) => s.toggleSnap);
  const gridSnapInches = useDesign((s) => s.gridSnapInches);
  const setGridSnapInches = useDesign((s) => s.setGridSnapInches);
  const furnitureSnapEnabled = useDesign((s) => s.furnitureSnapEnabled);
  const toggleFurnitureSnap = useDesign((s) => s.toggleFurnitureSnap);
  const designTool = useDesign((s) => s.designTool);
  const setDesignTool = useDesign((s) => s.setDesignTool);
  const measureActive = designTool === "measure";
  const activeWallIndex = useDesign((s) => s.activeWallIndex);
  const activeInteriorWall = useDesign((s) => s.activeInteriorWall);

  const isMobile = useIsMobile();
  const coarse = useCoarsePointer();
  const adjustModeOn = useFlag(TOUCH_ADJUST_MODE_FLAG);
  const adjustingId = useDesign((s) => s.adjustingFurnitureId);
  const enterAdjustMode = useDesign((s) => s.enterAdjustMode);
  const exitAdjustMode = useDesign((s) => s.exitAdjustMode);
  /** Bumped when a tapped knob opens the details sheet. */
  const [inspectorOpenRequest, setInspectorOpenRequest] = useState(0);
  const [focusField, setFocusField] = useState<KnobField | null>(null);
  const topbarSlot = useTopbarSlot();
  const toolsInTopbar = useLayoutMode() === "phoneLandscape" && topbarSlot != null;
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerFlash, setPickerFlash] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);

  // The feng shui guide's "Add furniture": phones open the picker sheet;
  // desktop already shows the picker in the side panel, so ring it briefly.
  useEffect(
    () =>
      subscribeFurniturePickerRequests(() => {
        if (isMobile) setPickerOpen(true);
        else setPickerFlash(true);
      }),
    [isMobile]
  );
  useEffect(() => {
    if (!pickerFlash) return;
    const t = window.setTimeout(() => setPickerFlash(false), 1600);
    return () => window.clearTimeout(t);
  }, [pickerFlash]);

  /** Phone tool row. On landscape phones it sits in the top bar, where
   *  Measure / Tools / 3D Render drop their labels below 800px; in portrait
   *  Measure / Tools go icon-only below 480px and 3D Render below 360px so
   *  the row fits beside Feng Shui. */
  function phoneTools(inTopbar: boolean) {
    const label = inTopbar ? "hidden min-[800px]:inline" : "hidden min-[480px]:inline";
    return (
      <>
        <button
          type="button"
          onClick={() => requestAi({ kind: "layout" })}
          className="pointer-events-auto h-11 shrink-0 px-3 min-[400px]:px-4 rounded-2xl bg-gradient-to-br from-violet-600 to-fuchsia-600 text-white ring-1 ring-violet-700 inline-flex items-center gap-2 text-body-sm font-semibold shadow-sm active:from-violet-700 active:to-fuchsia-700"
          data-testid="feng-shui-button"
        >
          <Sparkles className="size-4" />
          Feng Shui
        </button>
        <Render3DButton
          source="design"
          variant="phone"
          labelClassName={inTopbar ? "hidden min-[800px]:inline" : "hidden min-[360px]:inline"}
        />
        <div className={cn("flex shrink-0 items-center gap-2", !inTopbar && "ml-auto")}>
          <button
            type="button"
            onClick={() => setDesignTool(measureActive ? "select" : "measure")}
            aria-pressed={measureActive}
            aria-label="Measure"
            className={cn(
              "pointer-events-auto h-11 min-w-11 justify-center px-3.5 rounded-2xl ring-1 inline-flex items-center gap-2 text-body-sm font-medium transition-colors shadow-sm",
              measureActive
                ? "bg-ink-900 text-white ring-ink-900"
                : "bg-white text-ink-700 ring-ink-200 active:bg-ink-50"
            )}
            data-testid="design-measure-tool"
          >
            <Ruler className="size-4" />
            <span className={label}>Measure</span>
          </button>
          <button
            type="button"
            onClick={() => setToolsOpen(true)}
            aria-haspopup="dialog"
            aria-label="Tools"
            className="pointer-events-auto h-11 min-w-11 justify-center px-3.5 rounded-2xl bg-white ring-1 ring-ink-200 inline-flex items-center gap-2 text-body-sm font-medium text-ink-700 shadow-sm active:bg-ink-50"
            data-testid="design-tools-button"
          >
            <SlidersHorizontal className="size-4" />
            <span className={label}>Tools</span>
          </button>
        </div>
      </>
    );
  }
  const [activeCat, setActiveCat] = useState<PickerCategory>(
    FURNITURE_CATEGORIES[0].id
  );
  const premium = usePremium();
  const isPremium = premium.isPremium;
  const aiUsage = useAiUsage();
  const auth = useAuth();
  const [fengShuiOpen, setFengShuiOpen] = useState(false);
  // The sign-in surface the open AI sheet should report (set by the guides'
  // AI steps). Reset by the toolbar button.
  const [aiRequest, setAiRequest] = useState<{ surface?: SignInSurface }>({});
  // Set when AI is tapped before auth finishes restoring. Settled once.
  const pendingAiRef = useRef<AiIntent | null>(null);
  // Sign-in sheet for a visitor with no account. Null while closed.
  const [signInFor, setSignInFor] = useState<AiIntent | null>(null);
  // Re-render when the example's consultation id is recorded, so its
  // "Why it's here" (`isFengShuiExampleConsultation`) shows right away;
  // the landing hero also hides the Add button while it owns the screen.
  const fengShuiOb = useFengShuiOnboarding();
  const render3dOb = useRender3dOnboarding();
  const landingUp = isFengShuiLandingPending(fengShuiOb) || isRender3dLandingPending(render3dOb);
  const [importOpen, setImportOpen] = useState(false);
  // When set, the upsell overlay opens. Tracks WHICH feature the user
  // tried to use so the overlay can swap its headline; clears on close.
  const [upsellFeature, setUpsellFeature] = useState<PremiumFeatureKey | null>(
    null
  );
  const [upsellTrigger, setUpsellTrigger] = useState<UpsellTrigger>("quota");
  function openUpsell(feature: PremiumFeatureKey, trigger: UpsellTrigger) {
    setUpsellTrigger(trigger);
    setUpsellFeature(feature);
  }

  /** Open the feature sheet for someone who already has an account.
   *  Quota still short-circuits to the upsell. */
  function openAccountFeature(intent: AiIntent) {
    setSignInFor(null);
    const feature: PremiumFeatureKey =
      intent.kind === "layout" ? "ai_layout_feng_shui" : "product_url_import";
    if (shouldShowAiUpsell(isPremium, aiUsage)) {
      openUpsell(feature, "quota");
      return;
    }
    if (intent.kind === "layout") {
      setAiRequest({ surface: intent.surface });
      setFengShuiOpen(true);
    } else {
      setImportOpen(true);
    }
  }

  /** AI layout and product import. No account → sign-in sheet only.
   *  Account → the feature sheet (or the quota upsell). A tap while auth
   *  is still restoring waits, so a signed-in session doesn't flash
   *  sign-in. */
  function requestAi(intent: AiIntent) {
    // One sheet at a time: Import URL lives inside Add furniture.
    setPickerOpen(false);
    setToolsOpen(false);
    if (auth.status === "loading") {
      pendingAiRef.current = intent;
      return;
    }
    if (!hasAccount(auth.user)) {
      setFengShuiOpen(false);
      setImportOpen(false);
      setSignInFor(intent);
      return;
    }
    openAccountFeature(intent);
  }

  const requestAiRef = useRef(requestAi);
  useLayoutEffect(() => {
    requestAiRef.current = requestAi;
  });

  // The onboarding guides' AI steps use the same gate as the toolbar.
  // (The feng shui landing's saved example plays on the canvas instead.)
  useEffect(
    () =>
      subscribeAiLayoutRequests((req) => {
        requestAiRef.current({ kind: "layout", surface: req.surface });
      }),
    []
  );

  useEffect(() => {
    const intent = pendingAiRef.current;
    if (!intent || auth.status === "loading") return;
    pendingAiRef.current = null;
    requestAiRef.current(intent);
  }, [auth.status, auth.user]);

  // Signing out (or dropping to an anonymous session) while a feature
  // sheet is up replaces it with sign-in. Only a real loss of an account
  // counts — a sign-in that just succeeded may open the feature before
  // this render's auth snapshot has caught up.
  const hadAccountRef = useRef(false);
  useEffect(() => {
    if (auth.status === "loading") return;
    const account = hasAccount(auth.user);
    if (hadAccountRef.current && !account && (fengShuiOpen || importOpen)) {
      const intent: AiIntent = fengShuiOpen
        ? { kind: "layout", surface: aiRequest.surface }
        : { kind: "import" };
      setFengShuiOpen(false);
      setImportOpen(false);
      setSignInFor(intent);
    }
    hadAccountRef.current = account;
  }, [auth.status, auth.user, fengShuiOpen, importOpen, aiRequest.surface]);

  const importedProducts = useProducts((s) => s.products);
  const removeProduct = useProducts((s) => s.removeProduct);

  const customItems = useCustomFurniture((s) => s.items);
  const removeCustomItem = useCustomFurniture((s) => s.removeItem);
  // Furniture creator modal. `editingCustomItem` reshapes a library item.
  // `editingPlacedId` reshapes the copy on the canvas and does not write the library.
  const [creatorOpen, setCreatorOpen] = useState(false);
  const [editingCustomItem, setEditingCustomItem] =
    useState<CustomFurnitureItem | null>(null);
  const [editingPlacedId, setEditingPlacedId] = useState<string | null>(null);
  const editingPlaced =
    editingPlacedId != null
      ? (furniture.find(
          (f) => f.id === editingPlacedId && (f.shape?.length ?? 0) >= 3
        ) ?? null)
      : null;

  // Right-click context menu for layering controls. `targetId` is what the
  // menu commands act on; `position` holds the viewport coords the menu
  // should anchor at. Both are cleared together when the menu closes.
  const [menu, setMenu] = useState<{
    targetId: string;
    position: ContextMenuPosition;
  } | null>(null);
  const closeMenu = () => setMenu(null);

  const menuItems = useMemo<ContextMenuItem[]>(() => {
    if (!menu) return [];
    const list = furniture;
    const idx = list.findIndex((f) => f.id === menu.targetId);
    if (idx < 0) return [];
    const isTop = idx === list.length - 1;
    const isBottom = idx === 0;
    return [
      {
        id: "front",
        label: "Bring to front",
        icon: ArrowUpToLine,
        disabled: isTop,
        onSelect: () => reorderFurniture(menu.targetId, "front"),
      },
      {
        id: "forward",
        label: "Bring forward",
        icon: ChevronUp,
        disabled: isTop,
        onSelect: () => reorderFurniture(menu.targetId, "forward"),
      },
      {
        id: "backward",
        label: "Send backward",
        icon: ChevronDown,
        disabled: isBottom,
        divider: true,
        onSelect: () => reorderFurniture(menu.targetId, "backward"),
      },
      {
        id: "back",
        label: "Send to back",
        icon: ArrowDownToLine,
        disabled: isBottom,
        onSelect: () => reorderFurniture(menu.targetId, "back"),
      },
    ];
  }, [menu, furniture, reorderFurniture]);

  // History sheet: when opened by clicking the inspector's "From AI layout"
  // link, the consultation id is passed through so the sheet can auto-expand
  // and scroll to it. `null` means "no deep-link, default to the most recent".
  const [snapMenuOpen, setSnapMenuOpen] = useState(false);
  const snapMenuAnchorRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!snapMenuOpen) return;
    const onPointerDown = (e: PointerEvent) => {
      const anchor = snapMenuAnchorRef.current;
      if (!anchor) return;
      if (anchor.contains(e.target as Node)) return;
      setSnapMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setSnapMenuOpen(false);
    };
    window.addEventListener("pointerdown", onPointerDown, true);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("pointerdown", onPointerDown, true);
      window.removeEventListener("keydown", onKey);
    };
  }, [snapMenuOpen]);

  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyScrollToId, setHistoryScrollToId] = useState<string | null>(
    null
  );

  // Post-apply toast: when a new consultation appears, briefly surface a
  // "Layout applied · View rationale" banner so the user can deep-link into
  // the brand-new history entry without having to find the History button.
  const consultationCount = consultations?.length ?? 0;
  const latestConsultationId =
    consultationCount > 0
      ? (consultations as NonNullable<typeof consultations>)[
          consultationCount - 1
        ].id
      : null;
  const [toastConsultationId, setToastConsultationId] = useState<string | null>(
    null
  );
  // Don't flash the toast for consultations that existed when the component
  // first mounted (page reload after an old apply).
  const seenLatestRef = useRef<string | null>(latestConsultationId);
  useEffect(() => {
    // The toast only makes sense for users who can actually open the
    // history sheet to view the rationale — non-premium users would land
    // on the upsell overlay, which is the wrong flow for an apply that
    // already succeeded (premium override scenarios).
    if (!isPremium) return;
    if (!latestConsultationId) return;
    if (latestConsultationId === seenLatestRef.current) return;
    seenLatestRef.current = latestConsultationId;
    setToastConsultationId(latestConsultationId);
    const t = window.setTimeout(() => {
      setToastConsultationId((current) =>
        current === latestConsultationId ? null : current
      );
    }, 6000);
    return () => window.clearTimeout(t);
  }, [latestConsultationId, isPremium]);

  function openHistoryAt(id: string | null) {
    setHistoryScrollToId(id);
    setHistoryOpen(true);
  }

  const selected = useMemo(
    () => furniture.find((f) => f.id === selectedId) ?? null,
    [furniture, selectedId]
  );
  // "Why it's here" is Premium, except for the feng shui first run's
  // example layout (the ad's "it tells you why" beat) and the first real
  // layout on the visitor's own room while that first run is active — the
  // ad promised the explanation, so the first one is on us.
  const rationaleIsExample = isFengShuiExampleConsultation(selected?.rationale?.consultationId);
  const firstRealConsultationId = useMemo(
    () => consultations?.find((c) => !isFengShuiExampleConsultation(c.id))?.id ?? null,
    [consultations]
  );
  const rationaleIsFirstRun =
    fengShuiOb.active &&
    firstRealConsultationId != null &&
    selected?.rationale?.consultationId === firstRealConsultationId;
  const showRationale = isPremium || rationaleIsExample || rationaleIsFirstRun;

  function closeFurnitureSelection() {
    selectFurniture(null);
  }

  const canAdjust = isMobile && coarse && adjustModeOn;
  const adjusting = Boolean(selected && adjustingId === selected.id);

  /** A tapped knob opens the sheet with that field ready to type into. */
  function openKnobField(id: string, field: KnobField) {
    if (selected?.id !== id) return;
    setFocusField(field);
    setInspectorOpenRequest((n) => n + 1);
  }

  useEffect(() => {
    if (!focusField) return;
    let frames = 0;
    let raf = 0;
    const tryFocus = () => {
      const el = document.getElementById(`inspector-field-${focusField}`);
      if (el) {
        el.scrollIntoView({ block: "nearest" });
        el.focus();
        if (el instanceof HTMLInputElement) el.select();
        setFocusField(null);
        return;
      }
      if (++frames < 30) raf = requestAnimationFrame(tryFocus);
      else setFocusField(null);
    };
    raf = requestAnimationFrame(tryFocus);
    return () => cancelAnimationFrame(raf);
  }, [focusField, inspectorOpenRequest]);

  const area = polygonArea(room.points);

  function placePreset(p: FurniturePreset) {
    // place at centroid of bounding box, with a slight cascade offset to avoid overlapping items.
    const cx = room.points.reduce((s, q) => s + q.x, 0) / room.points.length;
    const cy = room.points.reduce((s, q) => s + q.y, 0) / room.points.length;
    const offset = (furniture.length % 6) * 8; // stagger by 8 inches
    addFurniture(presetToFurniture(p, cx + offset, cy + offset));
    setPickerOpen(false);
  }

  /** Open the furniture creator to author a brand-new piece. Replaces the old
   *  placeholder-drop flow — the user now sketches the footprint, names it, and
   *  picks a category before it's saved to their catalog and placed. */
  function openFurnitureCreator() {
    setEditingCustomItem(null);
    setEditingPlacedId(null);
    setCreatorOpen(true);
    setPickerOpen(false);
  }

  /** Open the creator pre-seeded to reshape an existing saved piece. */
  function editCustomItem(item: CustomFurnitureItem) {
    setEditingPlacedId(null);
    setEditingCustomItem(item);
    setCreatorOpen(true);
  }

  /** Open the creator to reshape the selected canvas piece. Save writes back
   *  onto that piece and leaves the library item alone. */
  function editPlacedShape(furnitureId: string) {
    setEditingCustomItem(null);
    setEditingPlacedId(furnitureId);
    setCreatorOpen(true);
  }

  /** Place a saved custom-furniture catalog item onto the canvas, copying its
   *  vector outline. Same centroid + cascade offset as `placePreset`. */
  function placeCustomItem(item: CustomFurnitureItem) {
    const cx = room.points.reduce((s, q) => s + q.x, 0) / room.points.length;
    const cy = room.points.reduce((s, q) => s + q.y, 0) / room.points.length;
    const offset = (furniture.length % 6) * 8;
    addFurniture(customItemToFurniture(item, cx + offset, cy + offset));
    setPickerOpen(false);
  }

  /**
   * Place an {@link ImportedProduct} onto the canvas. Routes by `kind`:
   *   - `furniture` → `addFurniture` at the room centroid (same staggered
   *     offset as catalog presets) and tags the new piece with
   *     `sourceUrl`/`sourcePrice`/`productId` so the inspector can show provenance.
   *   - `mount` → switch to wall view, pick the user's active wall (or
   *     wall 0 as a sensible default), and place the mount at the wall's
   *     midpoint, 60 inches off the floor. The user can drag it once
   *     placed.
   */
  function placeImportedProduct(product: ImportedProduct) {
    if (product.kind === "furniture") {
      const cx = room.points.reduce((s, q) => s + q.x, 0) / room.points.length;
      const cy = room.points.reduce((s, q) => s + q.y, 0) / room.points.length;
      const offset = (furniture.length % 6) * 8;
      // Imports classified by the extractor render with their template
      // (a sofa listing becomes a sofa, not a box). Older imports that
      // predate the field fall back to keywords in the listing name so a
      // "Corner sofa" still renders as an L instead of a box.
      const template = product.template ?? templateFromProductName(product.name);
      addFurniture({
        name: product.name,
        category: (product.category as FurnitureCategory) ?? "custom",
        width: product.width,
        depth: product.depth,
        height: product.height,
        x: cx + offset,
        y: cy + offset,
        rotation: 0,
        color: product.color,
        sourceUrl: product.sourceUrl,
        productId: product.id,
        ...listingPriceFields(product),
        model: { kind: "parametric", template, params: { ...(product.templateParams ?? {}) } },
        material: { id: product.material ?? TEMPLATES[template].defaultMaterial, tint: product.color },
        frontKind: TEMPLATES[template].frontKind,
        ...(product.capture ? { capture: { ...product.capture } } : {}),
      }, { source: "import" });
      setPickerOpen(false);
      return;
    }
    // mount: snap onto a wall. Prefer the user's active wall, fall back
    // to wall 0. The midpoint along the wall keeps the piece visible and
    // 60" floor offset matches the catalog defaults for mid-size mounts.
    const interior = activeInteriorWall;
    const host = interior
      ? {
          wallIndex: 0,
          interiorWallId: interior.wallId,
          interiorSegment: interior.segment,
        }
      : { wallIndex: activeWallIndex ?? 0 };
    const seg = resolveHostSegment(room, host);
    const len = seg?.len ?? wallLength(room.points, host.wallIndex);
    addMount({
      name: product.name,
      category: (product.category as WallMountCategory) ?? "custom",
      wallIndex: host.wallIndex,
      interiorWallId: interior?.wallId,
      interiorSegment: interior?.segment,
      alongWall: len / 2,
      fromFloor: 60,
      width: product.width,
      height: product.height,
      color: product.color,
      sourceUrl: product.sourceUrl,
      productId: product.id,
      ...listingPriceFields(product),
    });
    if (!interior && activeWallIndex === null) setActiveWallIndex(host.wallIndex);
    setView("wall");
    setPickerOpen(false);
  }

  /**
   * Called from {@link ImportProductSheet} after a successful save. Looks
   * up the freshly-stored product and places it on the canvas. Decoupled
   * from the sheet so the sheet stays focused on extraction/preview.
   */
  function placeImportedById(productId: string) {
    const product = useProducts.getState().products.find((p) => p.id === productId);
    if (!product) return;
    const sourceDomain = normalizeSourceDomain(product.sourceDomain);
    const productUrl = productUrlForAnalytics(product.sourceUrl);
    track("ai_product_import_placed", {
      template: product.template ?? null,
      dimension_source: product.dimensionSource ?? null,
      source: product.kind === "furniture" ? captureSource(product.capture) : "none",
      ...(sourceDomain ? { source_domain: sourceDomain } : {}),
      ...(productUrl ? { product_url: productUrl } : {}),
    });
    placeImportedProduct(product);
  }

  return (
    <div className="relative flex h-full">
      <main className="flex-1 relative bg-ink-50">
        {/* Stops above the mobile mode bar and anything docked on it (the
            onboarding coach bar), so the room re-fits instead of hiding. */}
        <div className="absolute inset-x-0 top-0 bottom-[var(--canvas-bottom)]">
          <RoomCanvas
            interactive
            showWallLabels
            enableWallSelect
            confirmWallOpen
            hideInfoBadge={isMobile || landingUp}
            fitInsets={landingUp ? (isMobile ? LANDING_CHROME : LANDING_CHROME_DESKTOP) : CANVAS_CHROME}
            onFurnitureContextMenu={(targetId, position) =>
              setMenu({ targetId, position })
            }
            onKnobTap={openKnobField}
            adjustHint={canAdjust}
          />
        </div>

        <ContextMenu
          position={menu?.position ?? null}
          items={menuItems}
          onClose={closeMenu}
          ariaLabel="Furniture layering"
        />

        <div className="absolute left-3 right-3 md:left-4 md:right-4 top-[calc(var(--topbar-h)+0.5rem)] z-10 flex flex-col gap-3 pointer-events-none">
          {isMobile ? (
            toolsInTopbar ? (
              createPortal(phoneTools(true), topbarSlot)
            ) : landingUp ? null : (
              // A landing's hero card takes this row.
              <div className="flex items-center gap-2">{phoneTools(false)}</div>
            )
          ) : (
          <div className="flex items-start gap-2 overflow-x-auto no-scrollbar pb-1 -mx-1 px-1 md:mx-0 md:px-0 md:overflow-visible">
            <Stat
              label="Room"
              value={formatArea(area, unit)}
              className="pointer-events-auto shrink-0"
            />
            <Stat
              label="Items"
              value={String(furniture.length)}
              className="pointer-events-auto shrink-0"
            />
            <button
              type="button"
              onClick={() =>
                setDesignTool(measureActive ? "select" : "measure")
              }
              className={`pointer-events-auto shrink-0 h-10 px-3 rounded-2xl ring-1 inline-flex items-center gap-2 text-xs font-medium transition-colors ${
                measureActive
                  ? "bg-ink-900 text-white ring-ink-900"
                  : "bg-white text-ink-700 ring-ink-200 hover:bg-ink-50"
              }`}
              title="Measure distance on the floor plan"
              data-testid="design-measure-tool"
            >
              <Ruler className="size-4" />
              Measure
            </button>
            <div className="pointer-events-auto ml-auto shrink-0 flex items-stretch gap-2 h-10">
              <button
                type="button"
                onClick={toggleFurnitureSnap}
                aria-pressed={furnitureSnapEnabled}
                aria-label="Snap to furniture"
                title="Snap to furniture"
                data-testid="snap-to-furniture"
                className={`h-10 px-3 rounded-2xl ring-1 inline-flex items-center gap-2 text-xs font-medium transition-colors ${
                  furnitureSnapEnabled
                    ? "bg-ink-900 text-white ring-ink-900"
                    : "bg-white text-ink-700 ring-ink-200 hover:bg-ink-50"
                }`}
              >
                <Magnet className="size-4" />
                <span className="hidden sm:inline">Furniture</span>
              </button>
            <div
              ref={snapMenuAnchorRef}
              className="relative inline-flex items-stretch h-10"
            >
              <button
                onClick={toggleSnap}
                className={`h-10 px-3 ring-1 inline-flex items-center gap-2 text-xs font-medium transition-colors ${
                  snapEnabled
                    ? "rounded-l-2xl bg-ink-900 text-white ring-ink-900"
                    : "rounded-2xl bg-white text-ink-700 ring-ink-200 hover:bg-ink-50"
                }`}
              >
                <Grid2x2Check className="size-4" />
                Snap
              </button>
              {snapEnabled && (
                <button
                  type="button"
                  onClick={() => setSnapMenuOpen((v) => !v)}
                  aria-haspopup="menu"
                  aria-expanded={snapMenuOpen}
                  aria-label={`Snap step: ${formatGridSnap(gridSnapInches)}`}
                  data-testid="snap-step-button"
                  className="h-10 pl-2 pr-2.5 -ml-px rounded-r-2xl bg-ink-900 text-white ring-1 ring-ink-900 inline-flex items-center gap-1 text-xs font-semibold tabular-nums hover:bg-ink-800 transition-colors"
                  title="Choose snap step"
                >
                  <span className="border-l border-white/20 pl-2">
                    {formatGridSnap(gridSnapInches)}
                  </span>
                  <ChevronDown className="size-3.5 opacity-80" />
                </button>
              )}
              {snapEnabled && snapMenuOpen && (
                <div
                  role="menu"
                  aria-label="Snap step"
                  data-testid="snap-step-menu"
                  className="absolute right-0 top-full mt-1.5 z-30 min-w-[9rem] rounded-xl bg-white text-ink-900 ring-1 ring-ink-200 shadow-xl shadow-ink-900/15 py-1.5"
                >
                  <div className="px-3 pt-1 pb-1 text-label font-semibold uppercase tracking-wide text-ink-500">
                    Snap step
                  </div>
                  {GRID_SNAP_OPTIONS_IN.map((opt) => {
                    const active = isSameSnap(opt, gridSnapInches);
                    return (
                      <button
                        key={opt}
                        type="button"
                        role="menuitemradio"
                        aria-checked={active}
                        onClick={() => {
                          setGridSnapInches(opt);
                          setSnapMenuOpen(false);
                        }}
                        data-testid={`snap-step-option-${snapStepOptionId(opt)}`}
                        className={`w-full px-3 py-1.5 text-left text-xs font-medium inline-flex items-center justify-between gap-3 transition-colors ${
                          active
                            ? "bg-ink-100 text-ink-900"
                            : "text-ink-700 hover:bg-ink-50"
                        }`}
                      >
                        <span>{formatGridSnap(opt)}</span>
                        <span className="text-label text-ink-500 tabular-nums">
                          {snapStepHint(opt)}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            </div>
            <button
              onClick={() => requestAi({ kind: "layout" })}
              className="pointer-events-auto shrink-0 h-10 px-3 rounded-2xl bg-gradient-to-br from-violet-600 to-fuchsia-600 text-white ring-1 ring-violet-700 inline-flex items-center gap-2 text-xs font-semibold shadow-sm hover:from-violet-700 hover:to-fuchsia-700 transition-colors"
              title="Feng Shui"
              data-testid="feng-shui-button"
            >
              <Sparkles className="size-4" />
              <span className="hidden sm:inline">Feng Shui</span>
            </button>
            <Render3DButton source="design" variant="toolbar" labelClassName="hidden sm:inline" />
            {isPremium && consultationCount > 0 && (
              <button
                onClick={() => openHistoryAt(null)}
                className="pointer-events-auto shrink-0 h-10 px-3 rounded-2xl bg-white text-ink-700 ring-1 ring-ink-200 inline-flex items-center gap-2 text-xs font-medium hover:bg-ink-50 transition-colors"
                title="View past layouts"
                data-testid="feng-shui-history-button"
              >
                <History className="size-4" />
                <span className="hidden sm:inline">
                  History
                  <span className="ml-1 text-ink-400 tabular-nums">
                    ({consultationCount})
                  </span>
                </span>
              </button>
            )}
          </div>
          )}

          {measureActive && (
            <div className="flex justify-center">
              <div
                className="pointer-events-auto bg-white/95 backdrop-blur ring-1 ring-ink-200 rounded-2xl px-4 py-3 shadow-lg max-w-sm text-center"
                data-testid="measure-tool-hint"
              >
                <div className="text-sm font-semibold text-ink-900">
                  Measure distance
                </div>
                <div className="text-xs text-ink-500 mt-0.5">
                  {isMobile ? (
                    "Tap two points on the floor plan. Tap again to start a new measurement."
                  ) : (
                    <>
                      Click two points on the floor plan. Click again to start a new
                      measurement. Press <span className="font-medium text-ink-700">Esc</span> to clear.
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {furniture.length === 0 && !measureActive && (
            <div className="flex justify-center">
              <div className="pointer-events-auto bg-white/95 backdrop-blur ring-1 ring-ink-200 rounded-2xl px-4 py-3 shadow-lg max-w-sm text-center">
                <div className="text-sm font-semibold text-ink-900">
                  Drop in your first piece
                </div>
                <div className="text-xs text-ink-500 mt-0.5">
                  Tap <span className="font-medium text-ink-700">Add furniture</span>, then drag it around. Tap any wall to mount things on it.
                </div>
              </div>
            </div>
          )}

          {isPremium && toastConsultationId && (
            <div className="flex justify-center">
              <div
                role="status"
                className="pointer-events-auto bg-ink-900 text-white rounded-full pl-3.5 pr-1 py-1 shadow-lg shadow-ink-900/30 flex items-center gap-2 text-body-sm"
                data-testid="feng-shui-applied-toast"
              >
                <Sparkles className="size-4 text-fuchsia-300" />
                <span>Layout applied.</span>
                <button
                  type="button"
                  onClick={() => {
                    openHistoryAt(toastConsultationId);
                    setToastConsultationId(null);
                  }}
                  className="h-10 px-3.5 rounded-full bg-white/10 hover:bg-white/15 text-white font-semibold"
                >
                  View rationale
                </button>
                {/* Separated from the open action so a dismiss can't land on it. */}
                <span aria-hidden className="h-5 w-px bg-white/20" />
                <button
                  type="button"
                  onClick={() => setToastConsultationId(null)}
                  aria-label="Dismiss"
                  className="h-10 w-10 inline-flex items-center justify-center rounded-full hover:bg-white/10"
                >
                  <X className="size-3.5" />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* FAB to open picker on mobile. The selection bar takes its row
            while something is selected. */}
        <div
          className={cn(
            "md:hidden absolute bottom-[var(--float-bottom)] right-[calc(0.75rem+var(--safe-right))] z-20",
            (selected || landingUp) && "hidden"
          )}
        >
          <Button
            variant="primary"
            size="lg"
            onClick={() => setPickerOpen(true)}
            className="shadow-lg shadow-ink-900/20"
          >
            <Plus className="size-5" />
            Add furniture
          </Button>
        </div>

        {/* Mobile selection inspector — minimized pill that expands over the canvas.
            Desktop uses the right context panel instead. */}
        {isMobile && (
          <MinimizableInspector
            open={!!selected}
            onClose={closeFurnitureSelection}
            itemId={selected?.id}
            openRequest={inspectorOpenRequest}
            modeBar={
              selected && adjusting ? (
                <AdjustModeBar piece={selected} unit={unit} onDone={() => exitAdjustMode("done")} />
              ) : undefined
            }
            title={selected?.name?.trim() || "Unnamed piece"}
            badge={
              selected?.rationale && showRationale
                ? {
                    icon: (
                      <span className="inline-flex size-5 items-center justify-center rounded-full bg-gradient-to-br from-violet-600 to-fuchsia-600 text-white">
                        <Sparkles className="size-3" aria-hidden />
                      </span>
                    ),
                    label: "placed by Feng Shui",
                  }
                : undefined
            }
            meta={
              selected
                ? `${formatLength(selected.width, unit)} × ${formatLength(selected.depth, unit)}`
                : undefined
            }
            swatch={
              selected ? (
                <div
                  className="size-5 rounded"
                  style={{ background: selected.color }}
                  aria-hidden
                />
              ) : null
            }
            quickActions={
              selected ? (
                <>
                  {canAdjust && (
                    <button
                      type="button"
                      onClick={() => enterAdjustMode(selected.id)}
                      aria-label="Adjust size and rotation"
                      data-testid="furniture-adjust"
                      className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-ink-700 active:bg-ink-100 transition-colors"
                    >
                      <Scaling className="size-[18px]" />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() =>
                      updateFurniture(selected.id, { rotation: (selected.rotation + 90) % 360 })
                    }
                    aria-label="Rotate 90°"
                    className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-ink-700 active:bg-ink-100 transition-colors"
                  >
                    <RotateCw className="size-[18px]" />
                  </button>
                  <button
                    type="button"
                    onClick={() => removeFurniture(selected.id)}
                    aria-label={`Delete ${selected.name || "piece"}`}
                    className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-rose-600 active:bg-rose-50 transition-colors"
                  >
                    <Trash2 className="size-[18px]" />
                  </button>
                </>
              ) : null
            }
            footer={
              selected ? (
                <FurnitureInspectorActions
                  selected={selected}
                  roomPoints={room.points}
                  onUpdate={(patch) => updateFurniture(selected.id, patch)}
                  onRemove={() => removeFurniture(selected.id)}
                />
              ) : null
            }
          >
            {selected && (
              <FurnitureInspectorContent
                selected={selected}
                unit={unit}
                showRationale={showRationale}
                rationaleIsExample={rationaleIsExample}
                compact
                onOpenHistory={openHistoryAt}
                onUpdate={(patch) => updateFurniture(selected.id, patch)}
                onEditShape={() => editPlacedShape(selected.id)}
              />
            )}
          </MinimizableInspector>
        )}
      </main>

      {/* Desktop context panel — catalog when nothing selected, properties when selected.
          Skipped on mobile so the canvas isn't pushed and the inspector pill is the
          sole surface for selected-item properties (matches what tests in jsdom
          expect, where Tailwind `hidden md:flex` is inert because CSS isn't applied). */}
      {!isMobile && (
      <DesktopSidePanel>
        {selected ? (
          <>
            <SelectionPanelHeader
              title={selected.name?.trim() || "Unnamed piece"}
              meta={`${formatLength(selected.width, unit)} × ${formatLength(selected.depth, unit)}`}
              swatch={
                <div
                  className="size-7 rounded-lg"
                  style={{ background: selected.color }}
                  aria-hidden
                />
              }
              onClose={closeFurnitureSelection}
            />
            <div className="flex-1 overflow-y-auto min-h-0">
              <FurnitureInspectorContent
                selected={selected}
                unit={unit}
                showRationale={showRationale}
                rationaleIsExample={rationaleIsExample}
                onOpenHistory={openHistoryAt}
                onUpdate={(patch) => updateFurniture(selected.id, patch)}
                onEditShape={() => editPlacedShape(selected.id)}
              />
            </div>
            <div className="shrink-0 border-t border-ink-100">
              <FurnitureInspectorActions
                selected={selected}
                roomPoints={room.points}
                onUpdate={(patch) => updateFurniture(selected.id, patch)}
                onRemove={() => removeFurniture(selected.id)}
              />
            </div>
          </>
        ) : (
          <>
            <div className="relative flex-1 flex flex-col min-h-0">
              <div className="px-5 pt-[calc(var(--topbar-h)+1rem)] pb-3 border-b border-ink-100 shrink-0">
                <div className="text-caption uppercase tracking-wide text-ink-400 font-semibold">
                  Step 2 of 3
                </div>
                <h2 className="text-xl font-bold text-ink-900 mt-1">Place furniture</h2>
                <p className="text-sm text-ink-500 mt-1">
                  Tap to add, drag to position. Select a piece to pull its edges, spin the
                  rotate grip, or change its shape and material.
                </p>
              </div>

              <FurniturePicker
                activeCat={activeCat}
                setActiveCat={setActiveCat}
                onPick={placePreset}
                onPickImported={placeImportedProduct}
                onRemoveImported={removeProduct}
                importedProducts={importedProducts}
                customItems={customItems}
                onPickCustom={placeCustomItem}
                onEditCustom={editCustomItem}
                onRemoveCustom={removeCustomItem}
                onOpenImport={() => requestAi({ kind: "import" })}
                onAddCustom={openFurnitureCreator}
              />

              <div
                aria-hidden
                data-testid="furniture-picker-flash"
                data-active={pickerFlash ? "true" : "false"}
                className={cn(
                  "pointer-events-none absolute inset-x-0 bottom-0 top-[var(--topbar-h)] z-20 ring-2 ring-inset ring-accent-500 bg-accent-500/5 transition-opacity duration-300 motion-reduce:transition-none",
                  pickerFlash ? "opacity-100" : "opacity-0"
                )}
              />
            </div>

            <div className="border-t border-ink-100 p-3 flex shrink-0">
              <Button
                variant="primary"
                size="md"
                className="w-full"
                onClick={() => useDesign.getState().setView("wall")}
              >
                <PanelRight className="size-4" />
                Mount things on walls
              </Button>
            </div>
          </>
        )}
      </DesktopSidePanel>
      )}

      <Sheet
        open={isMobile && pickerOpen}
        onClose={() => setPickerOpen(false)}
        title="Add furniture"
      >
        <FurniturePicker
          activeCat={activeCat}
          setActiveCat={setActiveCat}
          onPick={placePreset}
          onPickImported={placeImportedProduct}
          onRemoveImported={removeProduct}
          importedProducts={importedProducts}
          customItems={customItems}
          onPickCustom={placeCustomItem}
          onEditCustom={editCustomItem}
          onRemoveCustom={removeCustomItem}
          onOpenImport={() => requestAi({ kind: "import" })}
          onAddCustom={openFurnitureCreator}
        />
      </Sheet>

      <Sheet
        open={isMobile && toolsOpen}
        onClose={() => setToolsOpen(false)}
        title="Canvas tools"
      >
        <div className="flex flex-col gap-4 pt-1" data-testid="design-tools-sheet">
          <div className="grid grid-cols-2 gap-2">
            <Stat label="Room" value={formatArea(area, unit)} />
            <Stat label="Items" value={String(furniture.length)} />
          </div>
          <ToolToggle
            label="Snap to grid"
            description="Pieces land on even grid steps."
            checked={snapEnabled}
            onChange={toggleSnap}
          >
            {snapEnabled && (
              <Segmented
                size="md"
                value={snapStepOptionId(gridSnapInches)}
                onChange={(id) => {
                  const opt = GRID_SNAP_OPTIONS_IN.find((o) => snapStepOptionId(o) === id);
                  if (opt != null) setGridSnapInches(opt);
                }}
                options={GRID_SNAP_OPTIONS_IN.map((o) => ({
                  id: snapStepOptionId(o),
                  label: formatGridSnap(o),
                }))}
                className="mt-3 w-full [&>button]:flex-1"
              />
            )}
          </ToolToggle>
          <ToolToggle
            label="Snap to furniture"
            description="Line pieces up with each other's edges."
            checked={furnitureSnapEnabled}
            onChange={toggleFurnitureSnap}
          />
          {isPremium && consultationCount > 0 && (
            <Button
              variant="secondary"
              size="md"
              onClick={() => {
                setToolsOpen(false);
                openHistoryAt(null);
              }}
              data-testid="feng-shui-history-button"
            >
              <History className="size-4" />
              Past layouts ({consultationCount})
            </Button>
          )}
        </div>
      </Sheet>

      <ImportProductSheet
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onPlace={placeImportedById}
        onSignInRequired={() => {
          setImportOpen(false);
          setSignInFor({ kind: "import" });
        }}
        onPremiumRequired={(trigger) => {
          if (isPremium) return;
          setImportOpen(false);
          openUpsell("product_url_import", trigger);
        }}
      />

      {creatorOpen && (
        <FurnitureCreatorModal
          key={editingPlaced?.id ?? editingCustomItem?.id ?? "new"}
          editingItem={editingPlaced ? null : editingCustomItem}
          canvasSeed={
            editingPlaced?.shape
              ? {
                  name: editingPlaced.name,
                  category: editingPlaced.category,
                  width: editingPlaced.width,
                  depth: editingPlaced.depth,
                  height: editingPlaced.height,
                  color: editingPlaced.color,
                  shape: editingPlaced.shape,
                  model: editingPlaced.model,
                  material: editingPlaced.material,
                }
              : null
          }
          onCanvasCommit={
            editingPlaced
              ? (payload) => updateFurniture(editingPlaced.id, payload)
              : undefined
          }
          onClose={() => {
            setCreatorOpen(false);
            setEditingCustomItem(null);
            setEditingPlacedId(null);
          }}
          onSaved={(item) => {
            // Newly authored pieces drop straight onto the canvas; reshapes of
            // an existing library piece just update the catalog without re-placing.
            // Canvas-shape edits commit through onCanvasCommit instead.
            if (!editingCustomItem && !editingPlaced) placeCustomItem(item);
          }}
        />
      )}

      <FengShuiSheet
        open={fengShuiOpen}
        onClose={() => setFengShuiOpen(false)}
        signInSurface={aiRequest.surface}
        onOpenHistory={() => openHistoryAt(null)}
        onSignInRequired={() => {
          setFengShuiOpen(false);
          setSignInFor({ kind: "layout", surface: aiRequest.surface ?? "ai_sheet" });
        }}
        onPremiumRequired={(trigger) => {
          if (isPremium) return;
          setFengShuiOpen(false);
          openUpsell("ai_layout_feng_shui", trigger);
        }}
      />
      {signInFor && (
        <CreateAccountSheet
          open
          feature={signInFor.kind}
          surface={signInSurfaceFor(signInFor)}
          onClose={() => setSignInFor(null)}
          onSignedIn={() => openAccountFeature(signInFor)}
        />
      )}
      <FengShuiHistorySheet
        open={historyOpen}
        onClose={() => {
          setHistoryOpen(false);
          setHistoryScrollToId(null);
        }}
        scrollToId={historyScrollToId}
      />

      <PremiumUpsellOverlay
        open={upsellFeature !== null}
        feature={upsellFeature ?? "ai_layout_feng_shui"}
        trigger={upsellTrigger}
        onClose={() => setUpsellFeature(null)}
      />
    </div>
  );
}

function FurniturePicker({
  activeCat,
  setActiveCat,
  onPick,
  onPickImported,
  onRemoveImported,
  importedProducts,
  customItems,
  onPickCustom,
  onEditCustom,
  onRemoveCustom,
  onOpenImport,
  onAddCustom,
}: {
  activeCat: PickerCategory;
  setActiveCat: React.Dispatch<React.SetStateAction<PickerCategory>>;
  onPick: (p: FurniturePreset) => void;
  onPickImported: (product: ImportedProduct) => void;
  onRemoveImported: (id: string) => void;
  importedProducts: ImportedProduct[];
  /** The user's saved "furniture creator" pieces, shown under their category. */
  customItems: CustomFurnitureItem[];
  onPickCustom: (item: CustomFurnitureItem) => void;
  onEditCustom: (item: CustomFurnitureItem) => void;
  onRemoveCustom: (id: string) => void;
  /** Open the import sheet. The host gates this with the premium upsell
   *  overlay — callers should not invoke it without first checking the
   *  user's entitlement. */
  onOpenImport: () => void;
  /** Open the furniture creator to author a new piece. */
  onAddCustom: () => void;
}) {
  const unit = useDesign((s) => s.design.unit);

  // The "My imports" tab is shown only when the user has at least one
  // imported product on file. Premium isn't a prerequisite — they may
  // have imported things during a previous subscription and we still
  // want them visible / placeable.
  const showImportsTab = importedProducts.length > 0;
  // Snap the active category back to a valid value if "imports" was
  // selected and the user just deleted the last imported product.
  const effectiveCat: PickerCategory =
    activeCat === "imports" && !showImportsTab
      ? FURNITURE_CATEGORIES[0].id
      : activeCat;

  const items =
    effectiveCat === "imports"
      ? []
      : FURNITURE_PRESETS.filter((f) => f.category === effectiveCat);
  const customForCat =
    effectiveCat === "imports"
      ? []
      : customItems.filter((i) => i.category === effectiveCat);

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="px-3 pt-3 pb-2 border-b border-ink-100 sticky top-0 z-10 bg-white">
        <ScrollRow label="categories">
          {FURNITURE_CATEGORIES.map((c) => {
            const active = c.id === effectiveCat;
            return (
              <button
                key={c.id}
                onClick={() => setActiveCat(c.id)}
                aria-pressed={active}
                className={`shrink-0 h-9 px-3 rounded-full text-xs font-medium transition-colors ${
                  active
                    ? "bg-ink-900 text-white"
                    : "bg-ink-50 text-ink-600 hover:bg-ink-100"
                }`}
              >
                {c.label}
              </button>
            );
          })}
          {showImportsTab && (
            <button
              key="imports"
              onClick={() => setActiveCat("imports")}
              aria-pressed={effectiveCat === "imports"}
              data-testid="furniture-picker-imports-tab"
              className={`shrink-0 h-9 px-3 rounded-full text-xs font-medium transition-colors inline-flex items-center gap-1.5 ${
                effectiveCat === "imports"
                  ? "bg-ink-900 text-white"
                  : "bg-violet-50 text-violet-700 hover:bg-violet-100"
              }`}
            >
              <LinkIcon className="size-3.5" />
              My imports
              <span
                className={`text-label tabular-nums ${
                  effectiveCat === "imports" ? "text-ink-300" : "text-violet-400"
                }`}
              >
                {importedProducts.length}
              </span>
            </button>
          )}
        </ScrollRow>
      </div>
      <div className="grid grid-cols-2 gap-2 px-3 pt-3">
        <button
          type="button"
          onClick={onOpenImport}
          data-testid="furniture-picker-import-button"
          className="relative inline-flex h-11 items-center justify-center gap-1.5 rounded-xl bg-white text-body-sm font-semibold text-violet-700 ring-1 ring-violet-200 hover:bg-violet-50 active:bg-violet-100 transition-colors"
        >
          <LinkIcon className="size-4" />
          Import URL
        </button>
        <button
          type="button"
          onClick={onAddCustom}
          data-testid="furniture-picker-custom-button"
          className="inline-flex h-11 items-center justify-center gap-1.5 rounded-xl bg-white text-body-sm font-semibold text-ink-700 ring-1 ring-ink-200 hover:bg-ink-50 active:bg-ink-100 transition-colors"
        >
          <Pencil className="size-4" />
          Create furniture
        </button>
      </div>
      {effectiveCat === "imports" ? (
        <ImportedProductGrid
          products={importedProducts}
          unit={unit}
          onPick={onPickImported}
          onRemove={onRemoveImported}
          onOpenImport={onOpenImport}
        />
      ) : (
        <div className="flex-1 overflow-y-auto px-3 py-3 grid grid-cols-2 gap-2 auto-rows-min">
          {customForCat.map((item) => (
            <CustomFurnitureCard
              key={item.id}
              item={item}
              unit={unit}
              onPick={() => onPickCustom(item)}
              onEdit={() => onEditCustom(item)}
              onRemove={() => onRemoveCustom(item.id)}
            />
          ))}
          {items.map((p) => (
            <button
              key={p.name}
              onClick={() => onPick(p)}
              data-testid="furniture-preset-card"
              className="group flex flex-col items-stretch gap-2 p-3 rounded-2xl bg-ink-50 ring-1 ring-ink-100 hover:ring-ink-300 hover:bg-white hover:-translate-y-px hover:shadow-sm active:translate-y-0 active:scale-[0.99] transition-all text-left"
            >
              <div
                className="h-20 w-full rounded-lg flex items-center justify-center overflow-hidden"
                style={{ background: p.color + "14" }}
              >
                <FurniturePreview
                  furniture={presetPreviewFurniture(p)}
                  size={72}
                  pad={6}
                  lod={1}
                  textures={false}
                  shadow={false}
                  className="transition-transform duration-200 group-hover:scale-[1.04]"
                />
              </div>
              <div className="text-body-sm font-semibold text-ink-900 leading-tight">
                {p.name}
              </div>
              <div className="text-caption text-ink-400">
                {formatLength(p.width, unit)} × {formatLength(p.depth, unit)}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * A picker card for a user-authored "furniture creator" piece. Renders the
 * sketched vector outline as a thumbnail (instead of the category glyph used
 * for presets) plus reshape + delete affordances. Clicking the card body
 * places the piece on the canvas.
 */
function CustomFurnitureCard({
  item,
  unit,
  onPick,
  onEdit,
  onRemove,
}: {
  item: CustomFurnitureItem;
  unit: Unit;
  onPick: () => void;
  onEdit: () => void;
  onRemove: () => void;
}) {
  return (
    <div className="group relative flex flex-col items-stretch gap-2 p-3 rounded-2xl bg-ink-50 ring-1 ring-ink-100 hover:ring-ink-300 hover:bg-white hover:-translate-y-px hover:shadow-sm transition-all text-left">
      <button
        type="button"
        onClick={onPick}
        data-testid="custom-furniture-card"
        className="flex flex-col items-stretch gap-2 text-left"
      >
        <div
          className="h-20 w-full rounded-lg flex items-center justify-center overflow-hidden"
          style={{ background: item.color + "14" }}
        >
          <FurniturePreview
            furniture={item}
            size={72}
            pad={6}
            lod={1}
            textures={false}
            shadow={false}
            className="transition-transform duration-200 group-hover:scale-[1.04]"
          />
        </div>
        <div className="text-body-sm font-semibold text-ink-900 leading-tight pr-10">
          {item.name}
        </div>
        <div className="text-caption text-ink-400">
          {formatLength(item.width, unit)} × {formatLength(item.depth, unit)}
        </div>
      </button>
      <div className="absolute right-2 top-2 flex gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
        <button
          type="button"
          onClick={onEdit}
          aria-label={`Edit ${item.name}`}
          className="inline-flex size-7 items-center justify-center rounded-lg bg-white text-ink-500 ring-1 ring-ink-200 hover:text-ink-900 hover:bg-ink-50"
        >
          <Pencil className="size-3.5" />
        </button>
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Delete ${item.name}`}
          className="inline-flex size-7 items-center justify-center rounded-lg bg-white text-ink-400 ring-1 ring-ink-200 hover:text-rose-600 hover:bg-rose-50"
        >
          <Trash2 className="size-3.5" />
        </button>
      </div>
    </div>
  );
}

/**
 * Grid of the user's saved imported products. Lives inside the
 * {@link FurniturePicker} when the "My imports" tab is active. Each card
 * shows the imported product's name, dimensions, source domain, and a
 * delete affordance.
 */
function ImportedProductGrid({
  products,
  unit,
  onPick,
  onRemove,
  onOpenImport,
}: {
  products: ImportedProduct[];
  unit: Unit;
  onPick: (product: ImportedProduct) => void;
  onRemove: (id: string) => void;
  onOpenImport: () => void;
}) {
  if (products.length === 0) {
    return (
      <div className="flex-1 px-3 py-4">
        <div className="rounded-2xl bg-ink-50 ring-1 ring-ink-100 p-4 text-center">
          <div className="text-sm font-semibold text-ink-900">
            Nothing imported yet
          </div>
          <p className="mt-1 text-caption text-ink-500">
            Paste a product URL to pull in dimensions and add it to your
            canvas.
          </p>
          <div className="mt-3 flex justify-center">
            <Button variant="primary" size="sm" onClick={onOpenImport}>
              <LinkIcon className="size-3.5" />
              Import from URL
            </Button>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div
      className="flex-1 overflow-y-auto px-3 py-3 grid grid-cols-2 gap-2 auto-rows-min"
      data-testid="furniture-picker-imports-grid"
    >
      {products.map((p) => (
        <div
          key={p.id}
          className="group relative flex flex-col items-stretch gap-2 p-3 rounded-2xl bg-ink-50 ring-1 ring-ink-100 hover:ring-ink-300 hover:bg-white transition-all"
        >
          <button
            type="button"
            onClick={() => onPick(p)}
            className="text-left flex flex-col gap-2"
          >
            <div
              className="h-16 w-full rounded-lg flex items-center justify-center text-label uppercase tracking-wide font-semibold text-white/80"
              style={{ background: p.color }}
            >
              {p.kind === "mount" ? "Wall" : ""}
            </div>
            <div className="text-body-sm font-semibold text-ink-900 leading-tight line-clamp-2">
              {p.name}
            </div>
            <div className="text-caption text-ink-400">
              {formatLength(p.width, unit)}
              {" × "}
              {p.kind === "mount"
                ? formatLength(p.height, unit)
                : formatLength(p.depth, unit)}
              {p.needsManualReview && (
                <span className="ml-1 text-amber-600">·needs review</span>
              )}
            </div>
            {p.sourceDomain && (
              <div className="text-label text-ink-400 truncate">
                {p.sourceDomain}
              </div>
            )}
          </button>
          <button
            type="button"
            onClick={() => onRemove(p.id)}
            aria-label={`Delete ${p.name}`}
            className="absolute top-2 right-2 h-7 w-7 inline-flex items-center justify-center rounded-full bg-white/70 ring-1 ring-ink-200 text-ink-500 opacity-0 group-hover:opacity-100 hover:bg-rose-50 hover:text-rose-600 hover:ring-rose-200 transition-opacity"
          >
            <Trash className="size-3.5" />
          </button>
        </div>
      ))}
    </div>
  );
}

/** Price text to snapshot onto a placed piece. Blank listings omit the field. */
function listingPriceFields(product: ImportedProduct): { sourcePrice?: string } {
  const price = product.metadata?.price?.trim();
  return price ? { sourcePrice: price } : {};
}

/** The inspector's "Why it's here" callout. Reports a view once per piece. */
function RationaleCallout({
  rationale,
  isExample,
  onOpenHistory,
}: {
  rationale: NonNullable<Furniture["rationale"]>;
  isExample: boolean;
  onOpenHistory: (id: string | null) => void;
}) {
  // Report a view only when the callout is actually on screen: on phones
  // the sheet opens on its "peek" detent and this can sit below the fold.
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    let reported = false;
    const report = () => {
      if (reported) return;
      reported = true;
      track("ai_rationale_viewed", { source: isExample ? "example" : "ai" });
    };
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      report();
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          report();
          io.disconnect();
        }
      },
      { threshold: 0.6 }
    );
    io.observe(el);
    return () => io.disconnect();
    // Mounted per selected piece (keyed by the caller).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div className="px-4 pb-3">
      <div ref={ref} className="rounded-xl bg-ink-900 text-white p-3" data-testid="furniture-rationale">
        <div className="flex items-center gap-1.5 text-label uppercase tracking-wide font-semibold text-ink-300">
          <Sparkles className="size-3 text-fuchsia-300" />
          Why it's here
        </div>
        <p className="text-caption mt-1 leading-snug">{rationale.reason}</p>
        {isExample ? (
          <div className="mt-2 text-caption text-ink-300" data-testid="furniture-rationale-example">
            Example layout
          </div>
        ) : (
          <button
            type="button"
            onClick={() => onOpenHistory(rationale.consultationId)}
            className="mt-2 text-caption text-ink-300 hover:text-white underline underline-offset-2 decoration-ink-500/60 hover:decoration-white"
          >
            From Feng Shui · {formatRelative(rationale.at)}
          </button>
        )}
      </div>
    </div>
  );
}

function FurnitureInspectorContent({
  selected,
  unit,
  showRationale,
  rationaleIsExample,
  compact = false,
  onOpenHistory,
  onUpdate,
  onEditShape,
}: {
  selected: Furniture;
  unit: Unit;
  /** Drives whether the AI rationale callout renders: Premium users, plus
   *  the feng shui first run's example. Non-premium users with cached
   *  rationale (e.g. a lapsed subscriber) don't see it — there's no way
   *  for them to deep-link into the locked history sheet anyway. */
  showRationale: boolean;
  /** The rationale comes from the first run's saved example (no history link). */
  rationaleIsExample: boolean;
  /** Phone inspector: Shape and Material start folded so Size leads. */
  compact?: boolean;
  onOpenHistory: (id: string | null) => void;
  onUpdate: (patch: Partial<Furniture>) => void;
  onEditShape: () => void;
}) {
  return (
    <>
      <FurnitureNameField
        key={selected.id}
        furnitureId={selected.id}
        name={selected.name}
        onChange={(value) => onUpdate({ name: value })}
      />
      <ListingProvenance
        sourceUrl={selected.sourceUrl}
        sourcePrice={selected.sourcePrice}
        productId={selected.productId}
      />
      <PieceModelStatus selected={selected} onUpdate={onUpdate} />
      {showRationale && selected.rationale && (
        <RationaleCallout
          key={`${selected.id}:rationale`}
          rationale={selected.rationale}
          isExample={rationaleIsExample}
          onOpenHistory={onOpenHistory}
        />
      )}
      <InspectorSection
        label="Size"
        icon={<Ruler className="size-3.5" />}
        summary={`${formatLength(selected.width, unit)} × ${formatLength(selected.depth, unit)} × ${formatLength(selected.height, unit)}`}
      >
        <div className="flex flex-wrap gap-3">
          <NumberStepper
            label="Width"
            inputId="inspector-field-width"
            value={Math.round(selected.width)}
            onChange={(v) => onUpdate({ width: v })}
            min={6}
            max={240}
            step={1}
            suffix="in"
          />
          <NumberStepper
            label="Depth"
            inputId="inspector-field-depth"
            value={Math.round(selected.depth)}
            onChange={(v) => onUpdate({ depth: v })}
            min={6}
            max={240}
            step={1}
            suffix="in"
          />
          <NumberStepper
            label="Height"
            value={Math.round(selected.height)}
            onChange={(v) => onUpdate({ height: v })}
            min={1}
            max={120}
            step={1}
            suffix="in"
          />
        </div>
      </InspectorSection>
      <FurnitureShapeSection
        selected={selected}
        onUpdate={onUpdate}
        onEditShape={onEditShape}
        collapsible={compact}
      />
      <FurnitureMaterialSection selected={selected} onUpdate={onUpdate} collapsible={compact} />
    </>
  );
}

/** Rotate / face-in / delete row, pinned under the inspector's scroll area. */
function FurnitureInspectorActions({
  selected,
  roomPoints,
  onUpdate,
  onRemove,
}: {
  selected: Furniture;
  roomPoints: { x: number; y: number }[];
  onUpdate: (patch: Partial<Furniture>) => void;
  onRemove: () => void;
}) {
  const oriented = resolveFrontKind(selected) !== "none";
  const handleFaceIn = () => {
    const nearest = nearestWallSegment({ x: selected.x, y: selected.y }, roomPoints);
    if (!nearest) return;
    const a = roomPoints[nearest.index];
    const b = roomPoints[(nearest.index + 1) % roomPoints.length];
    const inward = inwardWallNormal(a, b, roomPoints);
    if (!inward) return;
    onUpdate({ rotation: rotationToFaceDirection(inward) });
  };

  return (
    <div className="px-2 py-2 flex items-center gap-1">
      <Button
        id="inspector-field-rotation"
        variant="secondary"
        size="touch"
        onClick={() => onUpdate({ rotation: (selected.rotation + 90) % 360 })}
      >
        <RotateCw className="size-4" />
        Rotate 90°
      </Button>
      <Button
        variant="ghost"
        size="touch"
        onClick={() => onUpdate({ rotation: (selected.rotation + 15) % 360 })}
      >
        +15°
      </Button>
      <Button
        variant="ghost"
        size="touch"
        onClick={handleFaceIn}
        disabled={!oriented}
        title={
          oriented
            ? "Rotate so the front faces away from the nearest wall"
            : "This piece has no defined front"
        }
      >
        <Compass className="size-4" />
        Face in
      </Button>
      <div className="flex-1 min-w-4" />
      <button
        type="button"
        className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-rose-600 hover:bg-rose-50 active:bg-rose-100 transition-colors"
        onClick={onRemove}
        aria-label={`Delete ${selected.name || "piece"}`}
      >
        <Trash2 className="size-4" />
      </button>
    </div>
  );
}

/**
 * Inspector field for editing a furniture piece's `name`. Custom pieces are
 * now named up-front in the furniture creator, so this is a plain rename
 * affordance (the legacy "name this unnamed placeholder" treatment is gone).
 *
 * `key={selected.id}` should be passed at the call site so the local draft
 * state resets cleanly when the user switches to a different piece.
 */
function FurnitureNameField({
  furnitureId,
  name,
  onChange,
}: {
  furnitureId: string;
  name: string;
  onChange: (next: string) => void;
}) {
  const inputId = `furniture-name-${furnitureId}`;
  return (
    <div className="px-4 pt-1 pb-3">
      <div className="flex items-center justify-between mb-1.5">
        <label
          htmlFor={inputId}
          className="text-label uppercase tracking-wide font-semibold text-ink-500"
        >
          Name
        </label>
      </div>
      <input
        id={inputId}
        type="text"
        value={name}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => useDesign.getState().beginGesture()}
        onBlur={() => useDesign.getState().endGesture()}
        onKeyDown={(e) => {
          if (e.key === "Enter") (e.currentTarget as HTMLInputElement).blur();
        }}
        placeholder="e.g. Reading nook chair"
        maxLength={120}
        data-testid="furniture-name-input"
        className="w-full h-9 px-3 rounded-lg ring-1 text-sm bg-white focus:outline-none focus:ring-2 transition-shadow ring-ink-200 focus:ring-ink-900 placeholder:text-ink-400"
      />
    </div>
  );
}
